import * as React from 'react';
import {FC, useEffect} from 'react';
import {NavLink} from 'react-router-dom';
import {Button} from '~/components/Button';
import {Trash2} from "lucide";
import {LucideIcon} from "~/components/LucideIcon";
import {TagsList} from '~/components/Controls/Tags';
import {useConfirm, useMessage} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import style from '~/components/ResourcePage/index.module.scss';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {useDataSource} from '~/lib/dataSource';
import {Copy, renderBoolean, renderToggle} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {sortByBoolean, sortByNumber, sortByString} from '~/lib/sort';
import {ClientsAbility} from '~/modules/Clients/ability';
import {sellerSourcesAdapter} from '~/modules/Clients/adapters';
import {sourceRecordsFilter} from '~/modules/Clients/filter';
import {sourcesLinks} from '~/modules/Sources/links';
import {prepareSourceSubmitData, toggleSource} from '~/modules/Sources/api';
import {SourceForm} from '~/modules/Sources/forms/SourceForm';
import {ImportPlacementsButton} from '~/modules/Sources/components/ImportPlacementsButton';
import {defaultSource} from '~/modules/Sources/models/Source';

interface ISellerSourcesParams {
    sellerId: string;
    viewOnly: boolean;
}

export const SellerSources: FC<ISellerSourcesParams> = (props) => {
    const {
        state,
        fetchRecords,
        createRecord,
        updateRecord,
        deleteRecord,
        setRecord
    } = useDataSource(sellerSourcesAdapter);
    const confirm = useConfirm();
    const message = useMessage();
    const ability = useAbility(ClientsAbility);

    const modal = useModals();

    const sourceCreate = modal.create(SourceForm, {
        lockSeller: true,
        onSubmit: (source, formikActions, close) => {
            createRecord(prepareSourceSubmitData(source))
                .then(close).catch(onSubmitError(formikActions));
        }
    });

    const sourceEdit = modal.create(SourceForm, {
        lockSeller: true,
        onSubmit: (source, formikActions, close) => {
            updateRecord(prepareSourceSubmitData(source))
                .then(close).catch(onSubmitError(formikActions));
        }
    });

    const fetchSources = () => fetchRecords({sellerId: props.sellerId});

    useEffect(fetchSources, []);

    return <>
        <Title className={style.titleMt}
            toolbar={<>{!props.viewOnly && ability.canCreateSource() && <ImportPlacementsButton key='import' sellerId={props.sellerId} onImported={fetchSources}/>}
            {!props.viewOnly && ability.canCreateSource() && <Button intent={Intent.PRIMARY} responsive
                key='create'
                leftIcon={<i
                    className='fa fa-edit'/>}
                disabled={state.isPending}
                onClick={() => sourceCreate.show({
                    source: {
                        ...defaultSource,
                        sellerId: props.sellerId
                    }
                })}>
                   Create
            </Button>}
            <Button intent={Intent.SUCCESS} responsive
                key='refresh'
                disabled={state.isPending}
                leftIcon={<i className='fa fa-refresh'/>}
                onClick={fetchSources}>
                       Refresh
            </Button></>}>Sources</Title>
        <TableSort defaultSort={{
            sort: sortByNumber,
            field: 'id',
            direction: TableSortDirection.DESC
        }} header columns={[
            {
                field: 'id',
                header: 'ID',
                sort: sortByNumber,
                small: true,
                hide: props.viewOnly
            },
            {
                field: 'integrationSourceId',
                header: 'Integration ID',
                cellAttrs: {
                    style: {
                        maxWidth: '120px',
                        overflow: 'hidden'
                    }
                },
                small: true,
                cell: source => <Copy text={source.integrationSourceId}>{source.integrationSourceId}</Copy>
            },
            {
                field: 'name',
                header: 'Name',
                sort: sortByString,
                cell: source => <NavLink to={sourcesLinks.source(source.id)}>{source.name}</NavLink>
            }, {
                field: 'tags',
                header: 'Tags',
                sort: sortByString,
                cell: source => <TagsList values={source.tags} size="small"/>,
                short: true
            }, {
                field: 'tmax',
                header: 'TMax',
                sort: sortByNumber
            }, {
                field: 'active',
                header: 'Active',
                cell: source => (!props.viewOnly && ability.canEditSource(source)) ? renderToggle(source.active,
                    () => confirm(`${source.active ? 'Dea' : 'A'}ctivate source?`, {
                        onOk: () => toggleSource(source, !source.active)
                            .then(setRecord).catch(() => {
                                message(`Can\'t ${source.active ? 'dea' : 'a'}ctivate agency`, {
                                    intent: Intent.DANGER,
                                    icon: <i className='fa fa-times'/>
                                }
                                );
                            }),
                        icon: <i className='fa fa-question'/>,
                        intent: Intent.WARNING
                    })) : renderBoolean(source.active),
                sort: sortByBoolean
            }, {
                small: true,
                hide: props.viewOnly,
                cell: (source) => <TableRowActions>
                    {ability.canEditSource(source) && (
                        <Button
                            small
                            variant="menu"
                            onClick={() => sourceEdit.show({source})}
                            leftIcon={<i className='fa fa-edit'/>}
                        >
                            Edit
                        </Button>
                    )}
                    {ability.canDeleteSource(source) && (
                        <Button
                            small
                            variant="menu"
                            intent={Intent.DANGER}
                            onClick={() => confirm('Delete source?', {
                                onOk: () => deleteRecord(source),
                                icon: <LucideIcon icon={Trash2} size={20}/>,
                                intent: Intent.DANGER
                            })}
                            leftIcon={<i className='fa fa-trash'/>}
                        >
                            Delete
                        </Button>
                    )}
                </TableRowActions>
            }]} records={state.records.filter(sourceRecordsFilter)} placeholder={state.isPending && <Progress/>}/>
    </>;
};
